import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavStack } from '@/hooks/useNavLayer';
import { useOnlineStore } from '@/lib/online-store';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { enhancedVoiceAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import {
  dismissAppUpdate,
  getAppUpdateSnapshot,
  requestAppUpdateApply,
  subscribeAppUpdate,
} from '@/lib/app-update';
import {
  notifyUpdateSafetyChanged,
  registerOverlayDepthReader,
} from '@/lib/update-safety';
import styles from './UpdateReadyLine.module.css';

/**
 * The quiet "a new version is ready" line. Mounted ONCE in App.tsx, inside
 * NavStackProvider (it reads overlay depth) and outside the auth/owner gates.
 *
 * Most people never see it: a stale page updates by itself after sitting in
 * the background, and this line is only offered when that has not happened
 * for hours, when something failed to load, or when an update is required.
 * It never reloads by itself; Refresh asks the scheduler.
 *
 * Hidden while any overlay is open, while offline, and during recording.
 *
 * Placement: on the home page it is a compact pill in the top-center status
 * slot, between the two corner buttons and above the first line of the
 * prayer, so it never touches the prayer text, the mic, Save or the dock. It
 * yields that slot to the pending-transcription pill and to active typing.
 * Everywhere else, and on the read-only home page (whose top slot holds the
 * maintenance notice), it sits bottom-center in the toast slot; transient
 * toasts paint above it.
 */
export default function UpdateReadyLine() {
  const { t } = useTranslation();
  const nav = useNavStack();
  const { pathname } = useLocation();
  const update = useSyncExternalStore(subscribeAppUpdate, getAppUpdateSnapshot);
  const overlayDepth = useSyncExternalStore(nav.subscribe, nav.length);
  const online = useOnlineStore((s) => s.online);
  const pendingCount = useAudioQueueStore((s) => s.pendingCount);

  // The nav coordinator lives inside the provider; hand the safety registry
  // a reader so "an overlay is open" can block a silent reload.
  useEffect(() => registerOverlayDepthReader(() => nav.length()), [nav]);
  useEffect(() => {
    notifyUpdateSafetyChanged();
  }, [overlayDepth]);

  if (!update.visible || overlayDepth > 0 || !online) return null;

  const home = pathname === '/' && !isCoreReadOnly();
  if (home) {
    // Same slot as PendingTranscriptionPill; and stay out of the way while
    // a prayer is being typed.
    if (update.typing) return null;
    if (enhancedVoiceAllowed() && pendingCount > 0) return null;
    // Below 360 CSS px (display zoom) the Spanish and Portuguese line wraps
    // to a second row and would reach the first line of the prayer. The
    // pill never sits over prayer text, so it waits until the composer is
    // empty; the page still updates by itself in the background meanwhile.
    if (window.innerWidth < 360) {
      const composer = document.querySelector<HTMLTextAreaElement>(
        'textarea[data-update-safe-draft]',
      );
      if (composer !== null && composer.value !== '') return null;
    }
  }

  return (
    <div
      className={`${styles.line} ${home ? styles.home : styles.page}`}
      role="status"
      aria-live="polite"
      data-update-line={home ? 'home' : 'page'}
    >
      <span className={styles.text}>
        {update.forced ? (
          <>
            {/* The compact home pill keeps to the one short line; the longer
                "required" sentence is used wherever there is room for it.
                CSS shows exactly one of the two. */}
            <span className={styles.short}>{t('appUpdate.ready')}</span>
            <span className={styles.full}>{t('appUpdate.required')}</span>
          </>
        ) : (
          t('appUpdate.ready')
        )}
      </span>
      <span className={styles.actions}>
        <button
          type="button"
          className={styles.refresh}
          disabled={update.busy}
          onClick={requestAppUpdateApply}
        >
          {t(update.busy ? 'appUpdate.finishing' : 'appUpdate.refresh')}
        </button>
        {update.dismissible && !update.forced && (
          <button
            type="button"
            className={styles.dismiss}
            aria-label={t('appUpdate.dismissAria')}
            onClick={dismissAppUpdate}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </span>
    </div>
  );
}
